const DEFAULT_BASE_URL = 'https://livedemo.sdk.hancom.com';

// baseUrl 결정 우선순위: 명시 인자 > VITE_PORTAL_BASE_URL_LIVEDEMO env > 브라우저 origin > 기본값
// → server에서는 .env의 livedemo URL을, 브라우저에서는 현재 호스트를 사용
function getDefaultBaseUrl() {
  if (typeof process !== 'undefined' && process.env?.VITE_PORTAL_BASE_URL_LIVEDEMO) {
    return process.env.VITE_PORTAL_BASE_URL_LIVEDEMO;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return DEFAULT_BASE_URL;
}

const SITE = {
  siteName: {
    ko: '한컴 SDK Live Demo',
    en: 'Hancom SDK Live Demo',
    ja: 'Hancom SDK ライブデモ',
  },
  ogLocale: {
    ko: 'ko_KR',
    en: 'en_US',
    ja: 'ja_JP',
  },
  defaultOgImage: '/images/og/og-sdk-1-hwp.png',
};

const FALLBACK_ROUTE_KEY = '/hwp-sdk';

const SUPPORTED_LANGS = ['ko', 'en', 'ja'];

const ROUTES = {
  '/hwp-sdk': {
    ogImage: '/images/og/og-sdk-1-hwp.png',
    i18n: {
      ko: {
        title: '한글 SDK live demo - HWP 실시간 편집 테스트',
        description: 'HWP 문서 생성과 편집 기능을 테스트할 수 있습니다.',
        keywords: '한글 SDK 데모, HWP API 테스트, 실시간 SDK 체험, 한컴 SDK',
      },
      en: {
        title: 'HWP SDK live demo - Real-time HWP editing test',
        description: 'Test HWP document creation and editing features.',
        keywords: 'HWP SDK Demo, HWP API Test, Online SDK Experience, Hancom SDK Demo',
      },
    },
  },

  '/hcell-sdk': {
    aliases: ['/spreadsheet-sdk'], 
    ogImage: '/images/og/og-sdk-2-hcell.png',
    i18n: {
      ko: {
        title: '한셀 SDK live demo - 스프레드시트 처리 실시간 테스트',
        description: '스프레드시트 데이터 처리와 셀 제어 기능을 테스트할 수 있습니다.',
        keywords: '한셀 SDK 데모, 엑셀 API 테스트, 데이터 처리 시연, 한컴 SDK',
      },
      en: {
        title: 'Hcell SDK live demo - Real-time Spreadsheet processing test',
        description: 'Test spreadsheet data processing and cell control features.',
        keywords: 'HCell SDK Demo, Excel API Test, Data Processing Demo, Hancom SDK Online',
      },
    },
  },

  '/docscompare-sdk': {
    ogImage: '/images/og/og-sdk-3-docs-compare.png',
    i18n: {
      ko: {
        title: '문서비교 SDK live demo - 문서 차이 분석 실시간 테스트',
        description: '두 문서를 비교해 텍스트, 표, 이미지 차이를 테스트할 수 있습니다.',
        keywords: '문서비교 데모, API 테스트, 문서 분석 시연, 한컴 SDK',
      },
      en: {
        title: 'Docs Compare SDK live demo - Real-time document difference analysis test',
        description: 'Compare documents and test differences in text, tables, and images.',
        keywords: 'Doc Compare Demo, API Test, Document Analysis, Diff Tool Demo',
      },
    },
  },

  '/ocr-sdk': {
    ogImage: '/images/og/og-sdk-4-ocr.png',
    i18n: {
      ko: {
        title: 'OCR live demo - 텍스트 인식 실시간 테스트',
        description: '이미지와 PDF에서 텍스트를 추출하는 OCR 기능을 테스트할 수 있습니다.',
        keywords:
          'OCR 데모, 실시간 문자 인식, OCR 성능 테스트, 한컴 SDK 체험, 온라인 OCR 테스트, OCR API 시연',
      },
      en: {
        title: 'OCR live demo - Real-time text recognition test',
        description: 'Test OCR features to extract text from images and PDF.',
        keywords: 'OCR Demo, Online Text Recognition, OCR API Test, AI OCR Experience',
      },
    },
  },

  '/dataloader': {
    ogImage: '/images/og/og-service-data-loader.png',
    i18n: {
      ko: {
        title: '데이터로더 live demo - 문서 구조화, AI 데이터 변환 실시간 테스트',
        description: 'HWP 문서의 표와 텍스트를 구조화된 데이터로 변환하는 기능을 테스트할 수 있습니다.',
        keywords:
          '데이터 로더 데모, AI 추출 테스트, 문서 구조화 체험, 한컴 데이터로더 API 시연, LLM 데이터 준비, 데이터 로더 체험',
      },
      en: {
        title: 'Data Loader live demo - Real-time document structuring and AI data conversion',
        description: 'Test converting document text and tables into structured data.',
        keywords: 'Data Loader Demo, AI Extraction Test, Document Parsing Demo, LLM RAG Prep',
      },
    },
  },

};

const ALIAS_TO_ROUTE = Object.entries(ROUTES).reduce((map, [key, route]) => {
  for (const alias of route.aliases ?? []) map[alias] = key;
  return map;
}, {});

function parsePath(pathname) {
  if (pathname === '/en' || pathname.startsWith('/en/')) {
    return { lang: 'en', routeKey: pathname.slice(3) || '/' };
  }
  if (pathname === '/ja' || pathname.startsWith('/ja/')) {
    return { lang: 'ja', routeKey: pathname.slice(3) || '/' };
  }
  return { lang: 'ko', routeKey: pathname || '/' };
}

function findRoute(routeKey) {
  if (ROUTES[routeKey]) return { key: routeKey, route: ROUTES[routeKey] };
  const aliased = ALIAS_TO_ROUTE[routeKey];
  if (aliased) return { key: aliased, route: ROUTES[aliased] };
  return null;
}

// 언어 prefix를 입힌 경로 생성. (ko, /hwp-sdk) → '/hwp-sdk', (en, /hwp-sdk) → '/en/hwp-sdk'
function buildLocalizedPath(lang, routeKey) {
  const prefix = lang === 'ko' ? '' : `/${lang}`;
  return routeKey === '/' ? prefix || '/' : `${prefix}${routeKey}`;
}

function buildAlternates(routeKey, baseUrl) {
  const alternates = {};
  for (const lang of SUPPORTED_LANGS) {
    alternates[lang] = baseUrl + buildLocalizedPath(lang, routeKey);
  }
  alternates['x-default'] = alternates.ko;
  return alternates;
}

function toAbsoluteOgUrl(image, baseUrl) {
  if (!image) return null;
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  return baseUrl + image;
}

export function resolveSeo(pathname, baseUrl) {
  const effectiveBaseUrl = baseUrl ?? getDefaultBaseUrl();
  const { lang, routeKey } = parsePath(pathname);
  const match = findRoute(routeKey) ?? {
    key: FALLBACK_ROUTE_KEY,
    route: ROUTES[FALLBACK_ROUTE_KEY],
  };
  const route = match.route;
  // 폴백 체인: 요청 언어 → en → ko
  const t = route.i18n[lang] ?? route.i18n.en ?? route.i18n.ko;

  return {
    lang,
    title: t.title,
    description: t.description,
    keywords: t.keywords,
    siteName: SITE.siteName[lang] ?? SITE.siteName.en,
    ogLocale: SITE.ogLocale[lang] ?? SITE.ogLocale.en,
    ogType: 'website',
    ogImage: toAbsoluteOgUrl(route.ogImage ?? SITE.defaultOgImage, effectiveBaseUrl),
    // canonical은 alias 해석 후 primary route key 기준. /spreadsheet-sdk → /hcell-sdk로 통합.
    // 알 수 없는 경로(fallback)도 /hwp-sdk로 통합되어 thin/duplicate content 신호 정리됨.
    canonical: effectiveBaseUrl + buildLocalizedPath(lang, match.key),
    alternates: buildAlternates(match.key, effectiveBaseUrl),
    noindex: route.noindex ?? false,
  };
}
